import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Send, TriangleAlert } from "lucide-react";
import { CONTACT_CATEGORIES, PRODUCTS } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- Field primitives ---------- */

export function Field({ id, label, required, optional, error, children, hint }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-charcoal mb-2">
        {label} {required ? <span className="text-charcoal/50" aria-hidden="true">*</span> : optional ? <span className="font-normal text-charcoal/50">(optional)</span> : null}
      </label>
      {children}
      {hint ? <p id={`${id}-hint`} className="mt-1.5 text-xs text-charcoal/50">{hint}</p> : null}
      {error ? (
        <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs font-semibold text-red-700" data-testid={`${id}-error`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

const inputCls = (invalid) =>
  `w-full bg-white border px-4 py-3 text-[15px] text-charcoal placeholder:text-charcoal/35 focus:outline-none focus:ring-2 focus:ring-lime focus:border-lime transition-colors duration-150 ${
    invalid ? "border-red-500" : "border-charcoal/25"
  }`;

function Consent({ id, error, text }) {
  const [checked, setChecked] = useState(false);
  return (
    <div>
      <label htmlFor={id} className="flex items-start gap-3 cursor-pointer">
        <input
          id={id}
          name="consent"
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          data-testid={`${id}-checkbox`}
          className="mt-1 h-4 w-4 accent-[#1C1D1F]"
        />
        <span className="text-sm text-charcoal/80 leading-relaxed">{text} <span aria-hidden="true">*</span></span>
      </label>
      {error ? (
        <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs font-semibold text-red-700" data-testid={`${id}-error`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/* ---------- Spam honeypot ---------- */

function Honeypot() {
  return (
    <div className="hp-field" aria-hidden="true">
      <label htmlFor="company_url">Company URL</label>
      <input id="company_url" name="company_url" type="text" tabIndex={-1} autoComplete="off" />
    </div>
  );
}

/* ---------- Shared submit state handling ---------- */

function useSubmit(endpoint, prepare) {
  const [state, setState] = useState({ status: "idle" });
  const submit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = prepare(form);
    const errors = validate(data);
    if (Object.keys(errors).length) {
      setState({ status: "error-validation", errors });
      return;
    }
    setState({ status: "sending" });
    try {
      const res = await fetch(`${API}/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "Request failed");
      setState({ status: "success", reference: body.reference });
    } catch (err) {
      setState({
        status: "error",
        message:
          err.message === "Failed to fetch"
            ? "We could not reach the server. Please try again or use a direct channel."
            : "Something went wrong while sending your enquiry. Please try again.",
      });
    }
  };
  return [state, submit];
}

function validate(data) {
  const errors = {};
  if (!data.name || data.name.trim().length < 2) errors.name = "Please enter your full name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email || "")) errors.email = "Please enter a valid business email.";
  if (!data.organization || data.organization.trim().length < 2) errors.organization = "Please enter your organization.";
  const msgKey = "use_case" in data ? "use_case" : "message";
  const msg = data.use_case || data.message;
  if (!msg || msg.trim().length < 10) errors[msgKey] = "Please tell us a little more (at least 10 characters).";
  if (!data.consent) errors.consent = "Please acknowledge the privacy notice.";
  return errors;
}

function SuccessPanel({ reference, title, children, testid }) {
  return (
    <div className="border border-lime bg-lime/10 p-8 md:p-10" data-testid={testid} role="status">
      <CheckCircle2 className="text-charcoal" size={36} aria-hidden="true" />
      <h3 className="font-display font-bold text-2xl mt-4">{title}</h3>
      <p className="mt-3 text-charcoal/75 leading-relaxed">{children}</p>
      <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Reference: {reference}</p>
    </div>
  );
}

/* ---------- Contact form ---------- */

export function ContactForm() {
  const [state, submit] = useSubmit("contact", formToData);

  function formToData(form) {
    const fd = new FormData(form);
    return {
      name: fd.get("name")?.trim() || "",
      email: fd.get("email")?.trim() || "",
      organization: fd.get("organization")?.trim() || "",
      category: fd.get("category"),
      message: fd.get("message")?.trim() || "",
      phone: fd.get("phone")?.trim() || "",
      industry: fd.get("industry")?.trim() || "",
      country: fd.get("country")?.trim() || "",
      consent: fd.get("consent") === "on",
      company_url: fd.get("company_url") || "",
    };
  }

  if (state.status === "success") {
    return (
      <SuccessPanel reference={state.reference} title="Thank you — your enquiry has been received." testid="contact-form-success">
        A member of the Softline Nigeria team will review your message and respond using the contact details you provided.
        Please keep your reference number for follow-up.
      </SuccessPanel>
    );
  }

  const errs = state.errors || {};
  return (
    <form onSubmit={submit} noValidate data-testid="contact-form" className="space-y-5">
      <Honeypot />
      <div className="grid md:grid-cols-2 gap-5">
        <Field id="cf-name" label="Full name" required error={errs.name}>
          <input id="cf-name" name="name" type="text" autoComplete="name" data-testid="contact-name-input" className={inputCls(!!errs.name)} aria-invalid={!!errs.name} aria-describedby={errs.name ? "cf-name-error" : undefined} />
        </Field>
        <Field id="cf-email" label="Business email" required error={errs.email}>
          <input id="cf-email" name="email" type="email" autoComplete="email" data-testid="contact-email-input" className={inputCls(!!errs.email)} aria-invalid={!!errs.email} aria-describedby={errs.email ? "cf-email-error" : undefined} />
        </Field>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <Field id="cf-organization" label="Organization" required error={errs.organization}>
          <input id="cf-organization" name="organization" type="text" autoComplete="organization" data-testid="contact-organization-input" className={inputCls(!!errs.organization)} aria-invalid={!!errs.organization} />
        </Field>
        <Field id="cf-category" label="Enquiry category" required>
          <select id="cf-category" name="category" data-testid="contact-category-select" className={inputCls(false)}>
            {CONTACT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field id="cf-message" label="Message" required error={errs.message}>
        <textarea id="cf-message" name="message" rows={5} data-testid="contact-message-input" className={inputCls(!!errs.message)} aria-invalid={!!errs.message} placeholder="Tell us about your requirements, timeline and any systems involved."></textarea>
      </Field>
      <div className="grid md:grid-cols-3 gap-5">
        <Field id="cf-phone" label="Telephone" optional>
          <input id="cf-phone" name="phone" type="tel" autoComplete="tel" data-testid="contact-phone-input" className={inputCls(false)} />
        </Field>
        <Field id="cf-industry" label="Industry" optional>
          <input id="cf-industry" name="industry" type="text" data-testid="contact-industry-input" className={inputCls(false)} />
        </Field>
        <Field id="cf-country" label="Country" optional>
          <input id="cf-country" name="country" type="text" autoComplete="country-name" defaultValue="Nigeria" data-testid="contact-country-input" className={inputCls(false)} />
        </Field>
      </div>
      <Consent
        id="cf-consent"
        error={errs.consent}
        text="I have read the Privacy Policy and agree that Softline Nigeria may process this information to respond to my enquiry."
      />
      {state.status === "error" ? (
        <div role="alert" data-testid="contact-form-error" className="flex items-start gap-3 border border-red-300 bg-red-50 p-4 text-sm text-red-800">
          <TriangleAlert size={18} className="mt-0.5 shrink-0" aria-hidden="true" /> {state.message}
        </div>
      ) : null}
      <button
        type="submit"
        disabled={state.status === "sending"}
        data-testid="contact-form-submit"
        className="inline-flex items-center gap-2 bg-lime text-charcoal font-semibold px-8 py-4 hover:bg-limelight transition-colors duration-200 disabled:opacity-60"
      >
        {state.status === "sending" ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : <Send size={18} aria-hidden="true" />}
        {state.status === "sending" ? "Sending…" : "Send Enquiry"}
      </button>
    </form>
  );
}

/* ---------- Demo / preview request form ---------- */

export function DemoForm({ productSlug = "softebox" }) {
  const [state, submit] = useSubmit("demo-request", formToData);

  function formToData(form) {
    const fd = new FormData(form);
    return {
      product: fd.get("product"),
      name: fd.get("name")?.trim() || "",
      email: fd.get("email")?.trim() || "",
      organization: fd.get("organization")?.trim() || "",
      use_case: fd.get("use_case")?.trim() || "",
      preferred_date: fd.get("preferred_date")?.trim() || "",
      consent: fd.get("consent") === "on",
      company_url: fd.get("company_url") || "",
    };
  }

  if (state.status === "success") {
    return (
      <SuccessPanel reference={state.reference} title="Preview request received." testid="demo-form-success">
        Thank you for your interest. Our product team will contact you to arrange a session. Submitting this form does not
        confirm a meeting — we will reach out to agree a time.
      </SuccessPanel>
    );
  }

  const errs = state.errors || {};
  return (
    <form onSubmit={submit} noValidate data-testid="demo-form" className="space-y-5">
      <Honeypot />
      <div className="grid md:grid-cols-2 gap-5">
        <Field id="df-product" label="Product" required>
          <select id="df-product" name="product" defaultValue={productSlug} data-testid="demo-product-select" className={inputCls(false)}>
            {PRODUCTS.map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field id="df-organization" label="Organization" required error={errs.organization}>
          <input id="df-organization" name="organization" type="text" autoComplete="organization" data-testid="demo-organization-input" className={inputCls(!!errs.organization)} aria-invalid={!!errs.organization} />
        </Field>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <Field id="df-name" label="Business contact name" required error={errs.name}>
          <input id="df-name" name="name" type="text" autoComplete="name" data-testid="demo-name-input" className={inputCls(!!errs.name)} aria-invalid={!!errs.name} />
        </Field>
        <Field id="df-email" label="Business email" required error={errs.email}>
          <input id="df-email" name="email" type="email" autoComplete="email" data-testid="demo-email-input" className={inputCls(!!errs.email)} aria-invalid={!!errs.email} />
        </Field>
      </div>
      <Field id="df-use-case" label="Intended use case" required error={errs.use_case} hint="Help us prepare: what process, system or problem should the session focus on?">
        <textarea id="df-use-case" name="use_case" rows={4} data-testid="demo-usecase-input" className={inputCls(!!errs.use_case)} aria-invalid={!!errs.use_case} placeholder="e.g. Delivering monthly statements to 50,000 customers with proof of delivery."></textarea>
      </Field>
      <Field id="df-date" label="Preferred meeting date" optional>
        <input id="df-date" name="preferred_date" type="date" data-testid="demo-date-input" className={inputCls(false)} />
      </Field>
      <Consent
        id="df-consent"
        error={errs.consent}
        text="I have read the Privacy Policy and agree that Softline Nigeria may process this information to arrange this session."
      />
      {state.status === "error" ? (
        <div role="alert" data-testid="demo-form-error" className="flex items-start gap-3 border border-red-300 bg-red-50 p-4 text-sm text-red-800">
          <TriangleAlert size={18} className="mt-0.5 shrink-0" aria-hidden="true" /> {state.message}
        </div>
      ) : null}
      <button
        type="submit"
        disabled={state.status === "sending"}
        data-testid="demo-form-submit"
        className="inline-flex items-center gap-2 bg-lime text-charcoal font-semibold px-8 py-4 hover:bg-limelight transition-colors duration-200 disabled:opacity-60"
      >
        {state.status === "sending" ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : <Send size={18} aria-hidden="true" />}
        {state.status === "sending" ? "Sending…" : "Submit Request"}
      </button>
    </form>
  );
}
